import { Component, computed, inject, input, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { MatCheckboxModule } from '@angular/material/checkbox';
import { ActionTypeEnum } from '@models/action';
import { GridFormViewComponent } from '@module-definition/form/grid-form-view/grid-form-view.component';
import { ActivityDetailsModel, PreferredField } from '@module-instance/activity/common';
import { DocumentDetailsStore } from '@module-instance/dynamic-module/document-details/common';
import { ModuleInstanceService } from '@module-instance/services';
import { compressGridRows } from '@shared/components/fields/common';
import { FroalaComponent } from '@shared/components/froala/froala.component';
import { SeeMoreComponent } from '@shared/components/see-more/see-more.component';
import { AppColors } from '@shared/consts';
import { NotificationService } from '@shared/services';
import { DictionaryStore } from '@signal-store/index';
import { makeKey } from '@signal-store/services/dictionary.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'cipo-activity-item-content',
  imports: [
    NgxSkeletonLoaderModule,
    TranslateModule,
    SeeMoreComponent,
    FlexLayoutModule,
    GridFormViewComponent,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    FroalaComponent,
  ],
  templateUrl: './activity-item-content.component.html',
})
export class ActivityItemContentComponent {
  private readonly moduleInstanceService = inject(ModuleInstanceService);
  private readonly dictionaryStore = inject(DictionaryStore);
  private readonly notify = inject(NotificationService);
  readonly dmStore = inject(DocumentDetailsStore);
  readonly ACTION_TYPES = ActionTypeEnum;
  activityDetails = input.required<ActivityDetailsModel>();

  isLoadingPreferredValue = signal(false);
  highlightedField = signal<number>(null);
  params = computed(() => this.dmStore.documents()[this.activityDetails().entityInstanceId].params);
  emailRecipients = computed(() => this.activityDetails()?.tos?.map(recipient => recipient.email) || []);
  moduleRolesDict = computed(() => {
    const { contractId, moduleId } = this.params();
    const key = makeKey({ contractId, moduleId });
    const roles = this.dictionaryStore.moduleRoles()[key]?.data ?? [];
    return roles.reduce((dict, role) => {
      dict[role.key] = role.value;
      return dict;
    }, {});
  });
  rolesToExclude = computed(
    () => this.activityDetails()?.roleToExcludeIds?.map(id => this.moduleRolesDict()[id]) || [],
  );
  availablePreferredFields = computed(() => {
    const { fields, preferredFields } = this.activityDetails();
    const fieldIds = fields?.map(f => f.id) || [];
    if (!fieldIds.length || !preferredFields?.length) {
      return [];
    }
    return preferredFields.filter(field => field.fieldIds.some(id => fieldIds.includes(id)));
  });
  activityFields = computed(() => {
    const fields = structuredClone(this.activityDetails()?.fields);
    if (!fields?.length) {
      return [];
    }
    const newFields = compressGridRows(fields);
    const availablePreferredFields = this.availablePreferredFields();
    if (!availablePreferredFields.length) {
      return newFields;
    }
    const highlightedField = this.highlightedField();
    const highlightedFieldIds = availablePreferredFields.find(field => field.key === highlightedField)?.fieldIds || [];
    return newFields.map(field => ({
      ...field,
      bgColor: highlightedFieldIds.includes(field.id) ? AppColors.primary_100 : undefined,
    }));
  });
  preferredValueDisabled = computed(() => {
    const { isPastRevision, isContractClosedOrArchived } =
      this.dmStore.documents()[this.activityDetails().entityInstanceId].info;
    return this.isLoadingPreferredValue() || isPastRevision || isContractClosedOrArchived;
  });

  toggleHighlight(item: PreferredField) {
    this.highlightedField.update(field => (field === item.key ? null : item.key));
  }

  togglePreferredValue({ key }: PreferredField) {
    this.isLoadingPreferredValue.set(true);
    this.moduleInstanceService
      .setPreferredValue({
        actionId: key,
        actionInstanceId: this.activityDetails()?.actionInstanceId,
        contractId: this.params().contractId,
      })
      .pipe(finalize(() => this.isLoadingPreferredValue.set(false)))
      .subscribe(() => this.notify.success('activity.preferredValueSet'));
  }
}
